import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const FireRegisterReport = ({ registerData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'name', label: 'Name' },
        { key: 'company', label: 'Company' },
        { key: 'time_in', label: 'Time In' },
        { key: 'time_out', label: 'Time Out' },
        { key: 'duration', label: 'Duration (Hours)' },
        { key: 'status', label: 'Status' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true);
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
            } else {
                logger.log("Invalid password.");
                registerData.length = 0;
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            showAlert({
                message: 'An error occurred while validating the password.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
        setOpenRequestPassword(false);
    };

    // Converts a time string ("HH:MM AM/PM", "HH:MM", or ISO) to "HH:MM" in 24-hour format
    const parseTo24Hour = (timeStr) => {
        if (typeof timeStr !== 'string') return null;
        let cleanTime = timeStr.trim();
        if (cleanTime.includes('T')) cleanTime = cleanTime.split('T')[1].split('.')[0];
        const amPmMatch = cleanTime.match(/\b(AM|PM)\b/i);
        const amPm = amPmMatch ? amPmMatch[1].toUpperCase() : null;
        cleanTime = cleanTime.replace(/\b(AM|PM)\b/i, '').trim();
        cleanTime = cleanTime.split('+')[0].split('Z')[0].replace(/-\d{2}:\d{2}$/, '').trim();
        if (cleanTime.includes(':')) {
            const parts = cleanTime.split(':');
            if (parts.length >= 2) {
                let hours = parseInt(parts[0], 10);
                const minutes = parseInt(parts[1], 10);
                if (!isNaN(hours) && !isNaN(minutes)) {
                    if (amPm === 'AM' && hours === 12) hours = 0;
                    else if (amPm === 'PM' && hours !== 12) hours += 12;
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                }
            }
        }
        return null;
    };

    const calculateDuration = (timeIn, timeOut) => {
        if (!timeIn || !timeOut) return '—';
        
        try {
            const formattedTimeIn = parseTo24Hour(timeIn) ? `${parseTo24Hour(timeIn)}:00` : null;
            const formattedTimeOut = parseTo24Hour(timeOut) ? `${parseTo24Hour(timeOut)}:00` : null;
            
            if (!formattedTimeIn || !formattedTimeOut) {
                logger.log('Failed to parse times:', timeIn, '->', formattedTimeIn, '|', timeOut, '->', formattedTimeOut);
                return '—';
            }
            
            const inTime = new Date(`1970-01-01T${formattedTimeIn}`);
            const outTime = new Date(`1970-01-01T${formattedTimeOut}`);
            
            // Check if dates are valid
            if (isNaN(inTime.getTime()) || isNaN(outTime.getTime())) {
                logger.log('Invalid date objects:', inTime, outTime);
                return '—';
            }
            
            let diffMs = outTime - inTime;
            
            // Handle case where time_out is next day
            if (diffMs < 0) {
                diffMs += 24 * 60 * 60 * 1000; // Add 24 hours
            }
            
            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            
            return `${hours}h ${minutes}m`;
        } catch (error) {
            logger.error('Error calculating duration:', error, 'timeIn:', timeIn, 'timeOut:', timeOut);
            return '—';
        }
    };

    const showData = () => {
        logger.log("Fire register data received:", registerData);
        
        if (!Array.isArray(registerData) || registerData.length === 0) {
            logger.log("No fire register data available");
            setTableData([]);
            setIsFiltered(true);
            return;
        }

        setIsFiltered(true);

        // Calculate summary statistics
        const stats = {
            totalEntries: registerData.length,
            staffEntries: registerData.filter(entry => entry.company === 'Customer 1st').length,
            visitorEntries: registerData.filter(entry => entry.company !== 'Customer 1st').length,
            completedVisits: registerData.filter(entry => entry.time_out).length,
            ongoingVisits: registerData.filter(entry => !entry.time_out).length,
        };
        setSummaryStats(stats);

        // Format filtered data
        const formatted = registerData.map((entry) => {
            const newEntry = {};
            displayColumns.forEach(({ key }) => {
                if (key === 'date') {
                    const dateObj = new Date(entry[key]);
                    newEntry[key] = isNaN(dateObj)
                        ? entry[key]
                        : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                } else if (key === 'time_in' || key === 'time_out') {
                    newEntry[key] = entry[key] ? (parseTo24Hour(entry[key]) ?? '—') : '—';
                } else if (key === 'duration') {
                    newEntry[key] = calculateDuration(entry.time_in, entry.time_out);
                } else if (key === 'status') {
                    newEntry[key] = entry.time_out ? 'Completed' : 'Ongoing';
                } else {
                    newEntry[key] = entry[key] ?? '—';
                }
            });
            return newEntry;
        });

        setTableData(formatted);
    };

    return (
        <div>
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h3>Fire Register Report</h3>
            </div>

            {/* Summary Statistics */}
            {isFiltered && Object.keys(summaryStats).length > 0 && (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                    gap: '15px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#17a2b8', margin: '0 0 5px 0' }}>{summaryStats.totalEntries}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Total Entries</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#28a745', margin: '0 0 5px 0' }}>{summaryStats.staffEntries}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Staff Entries</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#ffc107', margin: '0 0 5px 0' }}>{summaryStats.visitorEntries}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Visitor Entries</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#6f42c1', margin: '0 0 5px 0' }}>{summaryStats.completedVisits}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Completed</p>
                    </div>
                    <div style={{
                        background: isLightTheme ? '#fff' : '#333',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                        textAlign: 'center'
                    }}>
                        <h4 style={{ color: '#dc3545', margin: '0 0 5px 0' }}>{summaryStats.ongoingVisits}</h4>
                        <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Ongoing</p>
                    </div>
                </div>
            )}

            {isFiltered && (
                <div style={{ 
                    maxHeight: '70vh', 
                    overflowY: 'auto',
                    border: `1px solid ${isLightTheme ? '#ddd' : '#555'}`,
                    borderRadius: '8px'
                }}>
                    <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`} style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse'
                    }}>
                        <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                            <tr style={{ 
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                {displayColumns.map(({ key, label }) => (
                                    <th
                                        key={key}
                                        className="text-center"
                                        style={{
                                            padding: '12px 8px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.length > 0 ? (
                                tableData.map((row, index) => (
                                    <tr key={index}>
                                        {displayColumns.map(({ key }) => (
                                            <td
                                                key={key}
                                                className="text-center"
                                                style={{
                                                    padding: '10px 8px'
                                                }}
                                            >
                                                {row[key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={displayColumns.length}
                                        className="text-center"
                                        style={{
                                            padding: '20px',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        No fire register data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default FireRegisterReport;
